import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Check, ArrowRight, Send } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACKAGES = [
  {
    category: "Blog Posts",
    items: [
      { name: "500-word Blog Post", price: "$100" },
      { name: "1,000-word Blog Post", price: "$200" },
      { name: "1,500-word Blog Post", price: "$300" },
    ],
  },
  {
    category: "Article Writing",
    items: [
      { name: "1,000-word Article", price: "$250" },
      { name: "2,000-word Authority Article", price: "$500" },
    ],
  },
  {
    category: "Website Content",
    items: [
      { name: "Home Page Copy", price: "$300" },
      { name: "About Us Page", price: "$200" },
      { name: "5-Page Website Package", price: "$1,000" },
    ],
  },
  {
    category: "Email & Newsletter",
    items: [
      { name: "Newsletter Issue", price: "$150" },
      { name: "5-Email Welcome Sequence", price: "$500" },
    ],
  },
  {
    category: "White Papers",
    items: [
      { name: "White Paper (2,000–3,000 words)", price: "$1,000" },
      { name: "Research White Paper (4,000–5,000 words)", price: "$2,500+" },
    ],
  },
  {
    category: "Ghostwriting",
    items: [
      { name: "Leadership Article", price: "$500" },
      { name: "LinkedIn Article Package", price: "$750" },
    ],
  },
];

const SERVICE_TYPES = [
  "Blog Post", "Article Writing", "Website Content",
  "Email / Newsletter", "White Paper", "Ghostwriting", "Other",
];

export default function HireElizabeth() {
  const [form, setForm] = useState({
    name: "", email: "", company: "", service_type: "",
    description: "", budget: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.service_type || !form.description) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setSubmitted(true);
      toast.success("Inquiry sent! Elizabeth will be in touch within 1-2 business days.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-16 px-4 sm:px-6 max-w-5xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <p className="overline mb-4">Work with me</p>
            <h1 className="font-cormorant text-4xl sm:text-5xl font-semibold text-foreground leading-tight tracking-tight mb-5">
              Let's create content that{" "}
              <span className="italic text-brand-accent">actually converts</span>
            </h1>
            <p className="font-manrope text-base text-muted-foreground leading-relaxed mb-6">
              I help businesses, entrepreneurs, and organizations attract more customers through
              clear, engaging content. Every piece is tailored to your goals, audience, and brand
              voice — professionally written to help you stand out in a competitive marketplace.
            </p>
            <ul className="space-y-3 mb-8">
              {[
                "Blog posts, articles, newsletters & white papers",
                "Content tailored to your goals, audience & brand voice",
                "Member of AWAI's Circle of Success",
                "Research-backed, professional writing",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2.5 font-manrope text-sm text-foreground">
                  <Check size={16} className="text-brand-accent shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="rounded-2xl overflow-hidden aspect-[4/3] shadow-md"
          >
            <img
              src="/images/elizabeth-flatlay.jpg"
              alt="Elizabeth Neal — freelance writer"
              className="w-full h-full object-cover"
            />
          </motion.div>
        </div>
      </section>

      {/* Packages */}
      <section className="py-16 px-4 sm:px-6 bg-card border-y border-border">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="overline mb-3">Pricing</p>
            <h2 className="font-cormorant text-3xl sm:text-4xl font-semibold text-foreground mb-3">
              Writing packages & rates
            </h2>
            <p className="font-manrope text-sm text-muted-foreground max-w-lg mx-auto">
              All rates are starting prices. Complex topics, rush requests, or specialized industries
              may affect final pricing.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PACKAGES.map((pkg, i) => (
              <motion.div
                key={pkg.category}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08, duration: 0.4 }}
                className="bg-background rounded-xl border border-border p-6"
              >
                <p className="overline text-brand-accent mb-4">{pkg.category}</p>
                <ul className="space-y-3">
                  {pkg.items.map((item) => (
                    <li key={item.name} className="flex items-center justify-between gap-3">
                      <span className="font-manrope text-sm text-foreground">{item.name}</span>
                      <span className="font-manrope text-sm font-semibold text-brand-accent whitespace-nowrap">
                        {item.price}
                      </span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Inquiry Form */}
      <section className="py-16 px-4 sm:px-6 max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <p className="overline mb-3">Get in touch</p>
          <h2 className="font-cormorant text-3xl sm:text-4xl font-semibold text-foreground mb-3">
            Send an inquiry
          </h2>
          <p className="font-manrope text-sm text-muted-foreground">
            Tell me about your project. I respond within 1-2 business days.
          </p>
        </div>

        {submitted ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-16 px-8 bg-card border border-border rounded-2xl"
          >
            <div className="w-14 h-14 rounded-full bg-green-50 dark:bg-green-900/20 flex items-center justify-center mx-auto mb-5">
              <Check size={24} className="text-green-600" />
            </div>
            <h3 className="font-cormorant text-3xl font-semibold text-foreground mb-3">Inquiry received!</h3>
            <p className="font-manrope text-sm text-muted-foreground">
              Thank you for reaching out. Elizabeth will review your project and get back to you within 1-2 business days.
            </p>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-card border border-border rounded-2xl p-8 space-y-5" data-testid="inquiry-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="font-manrope text-xs font-semibold text-foreground mb-1.5 block">Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  required
                  data-testid="inquiry-name-input"
                  className="w-full px-4 py-2.5 rounded-lg border border-border bg-background font-manrope text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
                  placeholder="Your full name"
                />
              </div>
              <div>
                <label className="font-manrope text-xs font-semibold text-foreground mb-1.5 block">Email *</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  required
                  data-testid="inquiry-email-input"
                  className="w-full px-4 py-2.5 rounded-lg border border-border bg-background font-manrope text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
                  placeholder="you@company.com"
                />
              </div>
            </div>

            <div>
              <label className="font-manrope text-xs font-semibold text-foreground mb-1.5 block">Company / Brand</label>
              <input
                type="text"
                value={form.company}
                onChange={(e) => set("company", e.target.value)}
                data-testid="inquiry-company-input"
                className="w-full px-4 py-2.5 rounded-lg border border-border bg-background font-manrope text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
                placeholder="Your company or brand name"
              />
            </div>

            <div>
              <label className="font-manrope text-xs font-semibold text-foreground mb-1.5 block">Service type *</label>
              <select
                value={form.service_type}
                onChange={(e) => set("service_type", e.target.value)}
                required
                data-testid="inquiry-service-select"
                className="w-full px-4 py-2.5 rounded-lg border border-border bg-background font-manrope text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
              >
                <option value="">Select a service...</option>
                {SERVICE_TYPES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="font-manrope text-xs font-semibold text-foreground mb-1.5 block">Project description *</label>
              <textarea
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
                required
                rows={5}
                data-testid="inquiry-description-input"
                className="w-full px-4 py-2.5 rounded-lg border border-border bg-background font-manrope text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
                placeholder="Tell me about your project: topic, audience, goals, word count, deadline..."
              />
            </div>

            <div>
              <label className="font-manrope text-xs font-semibold text-foreground mb-1.5 block">Budget range</label>
              <input
                type="text"
                value={form.budget}
                onChange={(e) => set("budget", e.target.value)}
                data-testid="inquiry-budget-input"
                className="w-full px-4 py-2.5 rounded-lg border border-border bg-background font-manrope text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
                placeholder="e.g. $200–$500"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="inquiry-submit-btn"
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full bg-brand-accent text-white font-manrope text-sm font-medium hover:bg-brand-accent-hover disabled:opacity-60 transition-colors duration-200"
            >
              <Send size={16} />
              {submitting ? "Sending..." : "Send inquiry"}
            </button>
          </form>
        )}
      </section>

      <Footer />
    </div>
  );
}
